import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface SyncMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  is_from_agent: boolean;
  is_read: boolean;
  read_at: string | null;
  delivered_at: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
  created_at: string;
  is_pinned: boolean;
  pinned_at: string | null;
  pinned_by: string | null;
  is_starred: boolean;
  starred_at: string | null;
  starred_by: string | null;
  is_system_message?: boolean;
  is_deleted?: boolean;
  deleted_at?: string | null;
  deleted_by?: string | null;
  hidden_for?: string[];
}

interface UseMessageSyncOptions {
  conversationId: string | undefined;
  userId: string | undefined;
  pollingIntervalMs?: number;
  enabled?: boolean;
}

interface UseMessageSyncReturn {
  messages: SyncMessage[];
  setMessages: React.Dispatch<React.SetStateAction<SyncMessage[]>>;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

export function useMessageSync({
  conversationId,
  userId,
  pollingIntervalMs = 3000,
  enabled = true,
}: UseMessageSyncOptions): UseMessageSyncReturn {
  const [messages, setMessages] = useState<SyncMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const isMountedRef = useRef(true);
  const lastFetchRef = useRef<number>(0);
  
  // Use refs to avoid stale closures in callbacks
  const conversationIdRef = useRef(conversationId);
  const userIdRef = useRef(userId);
  
  // Keep refs in sync with props
  useEffect(() => {
    conversationIdRef.current = conversationId;
    userIdRef.current = userId;
  }, [conversationId, userId]);

  const fetchMessages = useCallback(async () => {
    const convId = conversationIdRef.current;
    const uid = userIdRef.current;
    
    if (!convId || !uid) return;

    console.log("[useMessageSync] Fetching messages for:", convId);
    
    const { data, error } = await supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", convId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("[useMessageSync] Fetch error:", error);
      return;
    }

    if (data && isMountedRef.current) {
      // Filter out messages hidden for this user
      const filteredMessages = (data as SyncMessage[]).filter(
        (m) => !m.hidden_for || !m.hidden_for.includes(uid)
      );
      console.log("[useMessageSync] Loaded", filteredMessages.length, "messages");
      setMessages(filteredMessages);
      lastFetchRef.current = Date.now();
    }
  }, []); // Empty deps - we use refs

  const refetch = useCallback(async () => {
    await fetchMessages();
  }, [fetchMessages]);

  // Initial fetch
  useEffect(() => {
    if (!enabled || !conversationId || !userId) {
      setIsLoading(false);
      return;
    }

    isMountedRef.current = true;
    setIsLoading(true);

    fetchMessages().finally(() => {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    });

    return () => {
      isMountedRef.current = false;
    };
  }, [conversationId, userId, enabled, fetchMessages]);

  // Realtime subscription for INSERT events (new messages)
  useEffect(() => {
    if (!enabled || !conversationId) return;

    const channel = supabase
      .channel(`msg-sync-${conversationId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const newMsg = payload.new as SyncMessage;
          console.log("[useMessageSync] INSERT received:", newMsg.id);
          setMessages((prev) => {
            if (prev.find((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId, enabled]);

  // Polling for updates (deletions, hidden_for changes)
  useEffect(() => {
    if (!enabled || !conversationId || !userId) return;

    // Re-fetch on visibility change (when app comes back to foreground)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("[useMessageSync] App visible, refetching");
        fetchMessages();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Polling interval
    const pollInterval = setInterval(() => {
      console.log("[useMessageSync] Poll tick - visible:", document.visibilityState);
      if (document.visibilityState === "visible") {
        fetchMessages();
      }
    }, pollingIntervalMs);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(pollInterval);
    };
  }, [conversationId, userId, pollingIntervalMs, enabled, fetchMessages]);

  return {
    messages,
    setMessages,
    isLoading,
    refetch,
  };
}
